import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { TradePill } from '../ui/trade-pill.js';
import { jobSiteLine } from '../../lib/job-site.js';
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Eye,
  Loader2,
  MapPin,
  Phone,
  CalendarOff,
  Plus,
  RefreshCw,
  ShoppingBasket,
  Sunrise,
  Sunset,
} from 'lucide-react';
import { JobPreviewModal } from './job-preview-modal.js';
import { ConfirmStartSheet } from './confirm-start-sheet.js';
import { ShoppingListSheet } from './shopping-list-sheet.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDialogA11y } from '../../lib/use-dialog-a11y.js';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { getOutboxDb, type SalesOrderOutboxEntry } from '../../lib/outbox.js';
import {
  ApiError,
  api,
  type FieldPhase,
  type JobLockedConflict,
  type SalesOrderSummary,
  type SalesType,
} from '../../lib/api.js';
import {
  durationLabel,
  windowLabel,
  FOP_UDF,
  readAssignment,
  readCompletedAt,
  readHelpers,
  shiftIsoDate,
  todayLocalIso,
} from '../../lib/dispatch-board.js';
import { useFieldJob } from '../../lib/field-job.js';
import { useTechVisits, withVisitCards } from '../../lib/visits.js';
import { useFieldJobsSettings } from '../../lib/settings.js';
import { useAvailabilityWindow, type Unavailable, unavailability } from '../../lib/availability.js';
import {
  ACTIVE_JOB_QUERY_KEY,
  formatTimer,
  phaseLabel,
  phasePillVariant,
  useActiveJob,
  useOpenEntrySeconds,
} from '../../lib/field-time.js';
import {
  orderTrade,
  tradeColor,
  tradeLabel,
  useSalesTypes,
  type Trade,
} from '../../lib/trade.js';
import { Pill } from '../ui/pill.js';
import { Feedback } from '../ui/feedback.js';

/** "Last completed" pill on Day expires this long after clock-out so a
 * tech who completed a job mid-morning isn't still staring at it after
 * lunch. The completeSummary itself stays in sessionStorage for the
 * Complete screen's resting state. */
const LAST_COMPLETED_TTL_MS = 30 * 60 * 1000;

/** How far the tech has to pull before the refresh fires. */
const PULL_REFRESH_PX = 64;

/**
 * Field-tech "My day" landing.
 *
 * Scoped to the logged-in user's dispatch assignments: query is gated on
 * `me.techId` (the FK to the dispatch-roster Tech), and the server applies
 * a `udf.FOP_TECH == me.techId` filter so a tech only sees their own
 * orders. If the user isn't linked to a Tech yet, the screen explains
 * how to get linked rather than dumping every open order in the system.
 */
export function DayScreen() {
  const salesTypes = useSalesTypes();
  const fieldJobs = useFieldJobsSettings();
  const typeUdfKey = fieldJobs.typeUdfKey;
  const startJob = useFieldJob((s) => s.startJob);
  const resumeActive = useFieldJob((s) => s.resumeActive);
  const completed = useFieldJob((s) => s.completeSummary);
  const queryClient = useQueryClient();

  // Server-enforced single-active-job lock. Tapping a stop here POSTs to
  // /field/time-entries/start; a 409 with code=JOB_ALREADY_ACTIVE means
  // the user already has a different job open and we surface a sheet
  // pointing them at it. The body shape is documented in the API DTO.
  const [locked, setLocked] = useState<JobLockedConflict | null>(null);
  // Preview / confirm-start / shopping-list state. All three sheets are
  // mutually exclusive — tapping a card with preview open closes it; the
  // confirm sheet's primary action is the same path as a direct tap. The
  // shopping list is a separate prep-time sheet, launched from the header.
  const [previewing, setPreviewing] = useState<{ spireId: number; orderNo: string; customerName: string } | null>(null);
  const [pendingStart, setPendingStart] = useState<PendingStart | null>(null);
  const [shoppingListOpen, setShoppingListOpen] = useState(false);
  const startMutation = useMutation({
    mutationFn: (input: { spireId: number; orderNo: string }) =>
      api.field.start({ spireSalesOrderId: input.spireId, phase: 'traveling' }),
    onSuccess: (_entry, vars) => {
      startJob(vars.spireId, vars.orderNo);
      void queryClient.invalidateQueries({ queryKey: ACTIVE_JOB_QUERY_KEY });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError && err.status === 409 && isJobLockedBody(err.body)) {
        setLocked(err.body);
      }
    },
  });
  // Escape hatch on the locked sheet: clock out of the held job WITHOUT
  // opening it. Needed when that order can't load any more — e.g. the office
  // invoiced it in Spire mid-visit, so the on-site screen 404s and the tech
  // was stuck unable to start anything (FPS 0000125628, 2026-10-28). `paused`
  // like "Done for today", so a still-live job isn't marked complete.
  const resetFieldJob = useFieldJob((s) => s.reset);
  const heldOrderId = useFieldJob((s) => s.currentOrderId);
  const clockOutHeldMutation = useMutation({
    mutationFn: () => api.field.stop({ finalPhase: 'paused' }),
    onSuccess: () => {
      if (locked && heldOrderId === locked.activeJobSpireId) resetFieldJob();
      setLocked(null);
      void queryClient.invalidateQueries({ queryKey: ACTIVE_JOB_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['field', 'day-list'] });
    },
  });
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api.me.get(signal),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const techId = meQuery.data?.techId ?? null;
  // Field-created jobs still in the outbox — shown as queued cards below
  // and keep the empty state from claiming "nothing today" meanwhile.
  const queuedRows = useLiveQuery(
    () => getOutboxDb().salesOrderOutbox.where('status').notEqual('sent').toArray(),
    [],
    [],
  );
  const queuedJobs = techId ? queuedRows.filter((r) => r.body.udf?.[FOP_UDF.tech] === techId) : [];

  // Server-side active-job state — drives the live timer + phase pill on
  // whichever card is currently being worked on.
  const activeQuery = useActiveJob();
  const activeSpireId = activeQuery.data?.activeJobSpireId ?? null;
  const liveSec = useOpenEntrySeconds(activeQuery.data ?? null);

  const onTapStop = (spireId: number, orderNo: string) => {
    // Tapping the tech's own active job: resume into the right phase-screen
    // instead of re-starting (which would close the existing TimeEntry and
    // open a fresh one in `traveling`, wiping phase history). Resume
    // skips the confirm sheet — the tech is already committed to this job.
    if (activeSpireId === spireId && activeQuery.data) {
      const desiredScreen =
        activeQuery.data.currentPhase === 'traveling' ? 'onway' : 'onsite';
      resumeActive(spireId, orderNo, desiredScreen);
      return;
    }
    // Fresh start: route through the confirm sheet so a stray tap doesn't
    // grab the active-job lock + fire a Spire write before the tech is
    // ready. Look up the card's metadata so the sheet shows the same
    // window / address the card was showing.
    // A multi-visit job is in the list once per day — take today's card so
    // the confirm sheet shows today's window, not another visit's.
    const order =
      all.find((o) => o.spireId === spireId && readAssignment(o.udf ?? {})?.date === todayIso) ??
      all.find((o) => o.spireId === spireId);
    if (!order) {
      // Defensive — shouldn't happen since the tap originated from a
      // rendered card, but if the cache shifted under us, fall through
      // to the bare start path.
      startMutation.mutate({ spireId, orderNo });
      return;
    }
    setPendingStart(buildPendingStart(salesTypes, order, typeUdfKey));
  };

  const onPreview = (spireId: number, orderNo: string, customerName: string) => {
    setPreviewing({ spireId, orderNo, customerName });
  };

  const ordersQuery = useQuery({
    queryKey: ['field', 'day-list', techId],
    queryFn: ({ signal }) =>
      api.salesOrders.search(
        {
          status: 'open',
          assignedToTechId: techId ?? undefined,
          // Fetch UDF so we can split in-progress vs done. Without this the
          // FOP_COMPLETED_AT marker isn't in the summary and we'd render
          // every order identically — which is exactly the bug a tech sees
          // when "nothing updates" after they clock out.
          includeUdf: true,
          // The API's max. A tech's assigned-and-still-open set keeps
          // growing — finished jobs stay open in Spire until invoiced —
          // and Spire returns oldest first, so a small page drops the
          // NEWEST jobs: at 20, FPS techs with 21-45 assigned orders lost
          // today's work from My Day (2026-10-08).
          limit: 500,
        },
        signal,
      ),
    enabled: !!techId,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  // Extra visits (multi-day work, return trips) this tech is on. A visit can
  // put them on a job whose own crew they aren't part of, so those orders
  // aren't in the list above and are fetched one by one.
  const todayIso = todayLocalIso();
  const visitsQuery = useTechVisits(techId, todayIso);
  const assignedIds = new Set((ordersQuery.data ?? []).map((o) => o.spireId));
  const visitOnlyIds = ordersQuery.data
    ? [...new Set((visitsQuery.data ?? []).map((v) => v.spireSalesOrderId))].filter((id) => !assignedIds.has(id))
    : [];
  const visitOnlyQueries = useQueries({
    queries: visitOnlyIds.map((id) => ({
      queryKey: ['sales-orders', 'detail', id] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) => api.salesOrders.getById(id, signal),
      staleTime: 30_000,
    })),
  });
  // The detail DTO is a superset of the summary fields the cards read.
  const visitOnlyOrders = visitOnlyQueries.flatMap((q) => (q.data ? [q.data as unknown as SalesOrderSummary] : []));

  const refetchAll = useCallback(() => {
    void ordersQuery.refetch();
    void visitsQuery.refetch();
    void activeQuery.refetch();
    void meQuery.refetch();
  }, [ordersQuery, visitsQuery, activeQuery, meQuery]);

  // Online/offline awareness — drives the synced pill. We use the browser's
  // navigator.onLine plus the query's own state so the pill reflects truth
  // even when navigator.onLine lies (it occasionally does on iOS Safari
  // when the modem is up but the carrier is throttling). Bias toward
  // "online" because false offline reads are scarier than missed offline.
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Pull-to-refresh — only triggers when the page is scrolled to the top
  // and the tech actively pulls past `PULL_REFRESH_PX`. Adds the rubber-
  // band feel without fighting iOS Safari's native rubber-band (we don't
  // preventDefault on the touchmove unless we're actively in a pull).
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const pullStartY = useRef<number | null>(null);
  const [pullDelta, setPullDelta] = useState(0);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onTouchStart = (e: TouchEvent) => {
      if (window.scrollY > 0) return;
      const t = e.touches[0];
      if (!t) return;
      pullStartY.current = t.clientY;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (pullStartY.current === null) return;
      const t = e.touches[0];
      if (!t) return;
      const dy = t.clientY - pullStartY.current;
      if (dy <= 0) {
        setPullDelta(0);
        return;
      }
      // Soft cap with a quasi-elastic feel — stop trying to pull past 100.
      const eased = Math.min(dy * 0.6, 100);
      setPullDelta(eased);
    };
    const onTouchEnd = () => {
      if (pullDelta >= PULL_REFRESH_PX) refetchAll();
      pullStartY.current = null;
      setPullDelta(0);
    };
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchEnd);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [pullDelta, refetchAll]);

  const all = withVisitCards(ordersQuery.data ?? [], visitOnlyOrders, visitsQuery.data ?? [], todayIso).sort(
    sortByRequiredDate,
  );
  // Group in-progress at the top, completed at the bottom. Completed jobs
  // keep their position relative to other completed jobs (chronological by
  // required date) — we don't try to sort by completion time.
  const inProgress = all.filter((o) => !readCompletedAt(o.udf ?? {}));
  // Only jobs completed TODAY (local time). Finished jobs stay open in
  // Spire until invoiced, so now that the list is no longer cut at 20 an
  // unfiltered "done" would show weeks of old visits.
  const doneToday = all.filter((o) => {
    const at = readCompletedAt(o.udf ?? {});
    if (!at) return false;
    const local = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`;
    return local === todayIso;
  });
  const grouped = groupByDate(inProgress, todayIso);
  const remaining = grouped.today.length;
  const dayTotals = computeDayTotals(grouped.today);

  return (
    <div ref={scrollerRef} className="space-y-4 relative">
      {pullDelta > 0 && (
        <div
          className="absolute inset-x-0 -top-2 flex items-center justify-center text-ink-muted pointer-events-none"
          style={{ transform: `translateY(${pullDelta - 32}px)`, transition: 'transform 80ms ease-out' }}
          aria-hidden
        >
          <Loader2
            className={`h-4 w-4 ${pullDelta >= PULL_REFRESH_PX ? 'animate-spin text-ink-accent' : ''}`}
          />
          <span className="ml-2 text-[11px]">
            {pullDelta >= PULL_REFRESH_PX ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      )}

      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs text-ink-muted">{todayLabel}</div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">My day</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {fieldJobs.enabled && techId && (
            <Link
              to="/jobs/new"
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium border bg-brand-50 text-ink-accent border-brand-200 hover:bg-brand-100"
              aria-label="Create a new job"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              New job
            </Link>
          )}
          {grouped.today.length > 0 && (
            <button
              type="button"
              onClick={() => setShoppingListOpen(true)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium border bg-surface text-ink hover:bg-surface-muted"
              style={{ borderColor: 'var(--color-border-tertiary)' }}
              aria-label="Open shopping list for today"
            >
              <ShoppingBasket className="h-3.5 w-3.5" aria-hidden />
              Shopping list ({grouped.today.length})
            </button>
          )}
          <button
            type="button"
            onClick={refetchAll}
            disabled={ordersQuery.isFetching || activeQuery.isFetching}
            aria-label="Refresh stops"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-muted hover:bg-surface-muted disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${ordersQuery.isFetching ? 'animate-spin' : ''}`} aria-hidden />
          </button>
          <SyncPill
            isOnline={isOnline}
            isFetching={ordersQuery.isFetching || activeQuery.isFetching}
            isError={ordersQuery.isError || meQuery.isError}
          />
        </div>
      </header>

      <div className="grid grid-cols-3 gap-2">
        <StatBox label="Stops" value={String(all.length)} />
        <StatBox
          label="Remaining"
          value={String(remaining)}
          sub={dayTotals.scheduledHours > 0 ? `${dayTotals.scheduledHours}h scheduled` : undefined}
        />
        <StatBox
          label="Done"
          value={String(doneToday.length)}
          sub={dayTotals.totalDollars > 0 ? `$${formatThousands(dayTotals.totalDollars)} today` : undefined}
        />
      </div>

      {techId && <DaysOffCard techId={techId} todayIso={todayIso} />}

      {completed && Date.now() - completed.completedAt < LAST_COMPLETED_TTL_MS && (
        <Pill variant="success" dot>
          Last completed — {completed.orderNo} ({completed.partsCount} parts, {completed.photoCount} photos)
        </Pill>
      )}

      <div className="space-y-4">
        {meQuery.isError && (
          <Feedback variant="error" size="lg">
            Couldn't load your profile: {meQuery.error.message}. Tap refresh
            once you have a signal, or sign out and back in if it persists.
          </Feedback>
        )}
        {meQuery.isSuccess && !techId && (
          <Feedback variant="info" size="lg">
            Your account isn't linked to a tech on the dispatch roster yet.
            Ask an admin to set this on your user (Admin → Users → Dispatch
            tech link) and refresh this screen.
          </Feedback>
        )}
        {techId && ordersQuery.isPending && (
          <>
            <div className="text-xs uppercase tracking-wider text-ink-subtle mb-2">Today</div>
            <DayListSkeleton />
          </>
        )}
        {techId && ordersQuery.isError && (
          <Feedback variant="error" size="lg">
            Couldn't load today's stops: {ordersQuery.error.message}
          </Feedback>
        )}
        {queuedJobs.length > 0 && <QueuedFieldJobs rows={queuedJobs} />}
        {techId && ordersQuery.data && all.length === 0 && queuedJobs.length === 0 && <EmptyDay />}

        {/* Overdue — past-dated stops that never got worked. Warning
           treatment so the tech sees them first; expanded by default. */}
        {grouped.overdue.length > 0 && (
          <DayBucket
            title="Overdue"
            tone="warning"
            orders={grouped.overdue}
            defaultExpanded={true}
            activeSpireId={activeSpireId}
            activePhase={activeQuery.data?.currentPhase ?? null}
            liveSec={liveSec}
            onTap={onTapStop}
            onPreview={onPreview}
            viewerTechId={techId}
          />
        )}

        {/* No date — assigned to the tech but no FOP_DATE. Rare, but if
           the dispatcher forgot to set a date this surfaces it instead of
           silently parking it under Today. */}
        {grouped.undated.length > 0 && (
          <DayBucket
            title="Date not set"
            tone="info"
            orders={grouped.undated}
            defaultExpanded={true}
            activeSpireId={activeSpireId}
            activePhase={activeQuery.data?.currentPhase ?? null}
            liveSec={liveSec}
            onTap={onTapStop}
            onPreview={onPreview}
            viewerTechId={techId}
          />
        )}

        {/* Today — the primary section. Always rendered (even when empty
           after grouping kicks in) so the tech sees their day's status. */}
        <DayBucket
          title="Today"
          tone="primary"
          orders={grouped.today}
          defaultExpanded={true}
          activeSpireId={activeSpireId}
          activePhase={activeQuery.data?.currentPhase ?? null}
          liveSec={liveSec}
          onTap={onTapStop}
          onPreview={onPreview}
          viewerTechId={techId}
          // Today is where the "next stop" highlight lives — when no
          // active job, the first card here gets the live treatment.
          // (Active card always wins regardless of which bucket it's in.)
          highlightFirstAsNext={true}
          emptyHint={
            grouped.future.length > 0
              ? 'Nothing scheduled for today — your next stop is coming up.'
              : doneToday.length > 0
                ? 'All today\'s stops are done.'
                : null
          }
        />

        {/* Future days — each gets its own collapsible bucket so a tech
           planning their week can peek tomorrow / Friday without losing
           today's focus. */}
        {grouped.future.map((bucket) => (
          <DayBucket
            key={bucket.date}
            title={friendlyFutureLabel(bucket.date, todayIso)}
            tone="muted"
            orders={bucket.orders}
            defaultExpanded={false}
            // A later visit of the job being worked right now is not itself
            // "active" — the live timer belongs to today's card.
            activeSpireId={null}
            activePhase={activeQuery.data?.currentPhase ?? null}
            liveSec={liveSec}
            onTap={onTapStop}
            onPreview={onPreview}
            viewerTechId={techId}
          />
        ))}

        {doneToday.length > 0 && (
          <DoneTodaySection
            orders={doneToday}
            onPreview={onPreview}
            viewerTechId={techId}
          />
        )}
      </div>

      <p className="text-center text-xs text-ink-muted">
        Tap the highlighted next stop to start. Done stops are read-only.
      </p>

      {locked && (
        <LockedJobSheet
          locked={locked}
          onClose={() => {
            clockOutHeldMutation.reset();
            setLocked(null);
          }}
          clockingOut={clockOutHeldMutation.isPending}
          clockOutError={clockOutHeldMutation.isError ? 'Could not clock out — check your signal and try again.' : null}
          onClockOut={() => clockOutHeldMutation.mutate()}
          onOpenActive={() => {
            // RESUME the running visit — never startJob() here: that
            // wipes locally-captured parts/photos/signature and forces
            // the screen back to Onway regardless of the real phase
            // (audit P0-10). Mirror the preview modal's resume path.
            const desiredScreen =
              activeQuery.data?.currentPhase === 'traveling' ? 'onway' : 'onsite';
            resumeActive(locked.activeJobSpireId, locked.activeJobOrderNo, desiredScreen);
            setLocked(null);
          }}
        />
      )}

      {previewing && (
        <JobPreviewModal
          spireId={previewing.spireId}
          orderNo={previewing.orderNo}
          customerNameFallback={previewing.customerName}
          onClose={() => setPreviewing(null)}
          // From the preview, "Start job" routes back through the fresh-
          // start flow (confirm sheet) when this isn't already the active
          // job. We close the preview first so the confirm sheet opens
          // cleanly. Active job → use the resume path instead.
          onStart={() => {
            const order = all.find((o) => o.spireId === previewing.spireId);
            if (!order) return;
            if (activeSpireId === previewing.spireId && activeQuery.data) {
              const desiredScreen =
                activeQuery.data.currentPhase === 'traveling' ? 'onway' : 'onsite';
              resumeActive(previewing.spireId, previewing.orderNo, desiredScreen);
            } else {
              setPendingStart(buildPendingStart(salesTypes, order, typeUdfKey));
            }
          }}
        />
      )}

      {pendingStart && (
        <ConfirmStartSheet
          customerName={pendingStart.customerName}
          orderNo={pendingStart.orderNo}
          scheduledWindowLabel={pendingStart.scheduledWindowLabel}
          addressLine={pendingStart.addressLine}
          trade={pendingStart.trade}
          pending={startMutation.isPending}
          onCancel={() => {
            if (startMutation.isPending) return;
            setPendingStart(null);
          }}
          onConfirm={() => {
            startMutation.mutate(
              { spireId: pendingStart.spireId, orderNo: pendingStart.orderNo },
              {
                onSuccess: () => setPendingStart(null),
                onError: () => {
                  // 409 JOB_ALREADY_ACTIVE surfaces via setLocked() in
                  // the mutation's onError above. Close this sheet so the
                  // locked sheet has the stage to itself.
                  setPendingStart(null);
                },
              },
            );
          }}
        />
      )}

      {shoppingListOpen && techId !== null && (
        <ShoppingListSheet
          ordersForToday={grouped.today}
          techId={techId}
          todayIso={todayIso}
          onClose={() => setShoppingListOpen(false)}
        />
      )}
    </div>
  );
}

function SyncPill({
  isOnline,
  isFetching,
  isError,
}: {
  isOnline: boolean;
  isFetching: boolean;
  isError: boolean;
}) {
  if (!isOnline) return <Pill variant="warning" dot>Offline</Pill>;
  if (isError) return <Pill variant="danger" dot>Sync error</Pill>;
  if (isFetching) return <Pill variant="info" dot>Syncing…</Pill>;
  return <Pill variant="success" dot>Synced</Pill>;
}

function DoneTodaySection({
  orders,
  onPreview,
  viewerTechId,
}: {
  orders: ReadonlyArray<SalesOrderSummary>;
  onPreview: (spireId: number, orderNo: string, customerName: string) => void;
  viewerTechId: string | null;
}) {
  const salesTypes = useSalesTypes();
  const typeUdfKey = useFieldJobsSettings().typeUdfKey;
  // Collapsed by default — by mid-day a tech may have 5+ completed stops
  // and that section drowning out the in-progress list is the opposite of
  // what they need on quick checks. The summary chip stays visible so the
  // count is still glanceable.
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-5">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-ink-subtle hover:text-ink mb-2"
        aria-expanded={expanded}
      >
        <span>Done today ({orders.length})</span>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
      {expanded && (
        <ul className="space-y-2">
          {orders.map((order) => {
            const trade = orderTrade(salesTypes, order, typeUdfKey);
            const leadId = order.udf?.[FOP_UDF.tech]?.trim() ?? null;
            const isHelper = viewerTechId !== null && leadId !== null && leadId !== viewerTechId;
            return (
              <li key={order.spireId}>
                <StopCard
                  order={order}
                  trade={trade}
                  isNext={false}
                  isActive={false}
                  liveSec={null}
                  activePhase={null}
                  isDone={true}
                  isHelper={isHelper}
                  onTap={() => { /* completed stops are read-only */ }}
                  onPreview={() => onPreview(order.spireId, order.orderNo, order.customer.name)}
                />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function EmptyDay() {
  const now = new Date();
  const isMorning = now.getHours() < 11;
  return (
    <div
      className="rounded-lg border bg-surface px-6 py-8 text-center"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      {isMorning ? (
        <Sunrise
          className="h-8 w-8 mx-auto text-amber-500"
          style={{ color: 'var(--trade-sales)' }}
          aria-hidden
        />
      ) : (
        <Sunset
          className="h-8 w-8 mx-auto text-amber-500"
          style={{ color: 'var(--trade-sales)' }}
          aria-hidden
        />
      )}
      <div className="mt-2 text-sm font-medium text-ink">
        {isMorning ? 'Nothing on the board yet' : "You're done for the day"}
      </div>
      <div className="mt-1 text-[12px] text-ink-muted">
        {isMorning
          ? 'Dispatch hasn\'t scheduled anything to you. Pull down to refresh, or check back at 8 AM.'
          : 'No more open stops assigned to you. Nice work.'}
      </div>
    </div>
  );
}

function LockedJobSheet({
  locked,
  onClose,
  onOpenActive,
  onClockOut,
  clockingOut,
  clockOutError,
}: {
  locked: JobLockedConflict;
  onClose: () => void;
  onOpenActive: () => void;
  onClockOut: () => void;
  clockingOut: boolean;
  clockOutError: string | null;
}) {
  const dialogRef = useDialogA11y<HTMLDivElement>(onClose);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="A job is already in progress"
      className="fixed inset-0 z-40 bg-slate-950/40 flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl shadow-xl p-4 space-y-3"
        style={{ backgroundColor: 'var(--color-elevated-surface)' }}
      >
        <div className="text-sm font-medium text-ink">Job already in progress</div>
        <div className="text-sm text-ink-muted">
          You have job <span className="font-mono">{locked.activeJobOrderNo}</span> open. Clock out
          of it before starting another stop (pausing keeps it locked to you).
        </div>
        {clockOutError && <div className="text-xs text-red-700">{clockOutError}</div>}
        <div className="flex flex-wrap gap-2 justify-end pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm text-ink-muted hover:bg-surface-muted"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={onClockOut}
            disabled={clockingOut}
            className="rounded-md border px-3 py-2 text-sm font-medium text-ink hover:bg-surface-muted disabled:opacity-50"
            style={{ borderColor: 'var(--color-border-secondary)' }}
          >
            {clockingOut ? 'Clocking out…' : `Clock out of ${locked.activeJobOrderNo}`}
          </button>
          <button
            type="button"
            onClick={onOpenActive}
            className="rounded-md px-3 py-2 text-sm font-medium text-white"
            style={{ backgroundColor: 'var(--color-text-info)' }}
          >
            Open {locked.activeJobOrderNo}
          </button>
        </div>
      </div>
    </div>
  );
}

function isJobLockedBody(body: unknown): body is JobLockedConflict {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as Record<string, unknown>;
  return (
    b.code === 'JOB_ALREADY_ACTIVE' &&
    typeof b.activeJobSpireId === 'number' &&
    typeof b.activeJobOrderNo === 'string'
  );
}

function StatBox({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div
      className="rounded-md bg-surface px-3 py-2 border"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="text-lg font-semibold text-ink tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-ink-subtle truncate">{sub}</div>}
    </div>
  );
}

interface StopCardProps {
  order: SalesOrderSummary;
  trade: Trade;
  /** Renders the deep-blue "live" treatment regardless of whether a
   * server-side timer is running. Used for the first upcoming stop when
   * no job is active, AND for the currently-active job. */
  isNext: boolean;
  /** True when this card represents the tech's currently-active job
   * (server has an open TimeEntry for it). When true, isNext is also
   * true and the card shows a live elapsed timer. */
  isActive: boolean;
  /** Live seconds-since-start of the open TimeEntry. Null when this card
   * isn't the active job. */
  liveSec: number | null;
  /** Current phase from the open TimeEntry. Null when this card isn't
   * the active job. Falls back to FOP_PHASE UDF (set on transitions) for
   * non-active cards. */
  activePhase: FieldPhase | null;
  isDone: boolean;
  /** True when this card represents an order where the viewer is a HELPER
   * — i.e. they're in FOP_HELPERS but not FOP_TECH. The card renders a
   * "Helper" badge so the tech knows workflow ownership (phase, signature,
   * completed flag) sits with the lead. */
  isHelper: boolean;
  onTap: () => void;
  /** Opens the read-only Job preview modal — distinct from onTap which
   * starts/resumes the job. The card body fires onTap; the eye icon
   * fires onPreview. Both surfaces stop propagation so they never
   * compete with one another. */
  onPreview: () => void;
}

function StopCard({
  order,
  trade,
  isNext,
  isActive,
  liveSec,
  activePhase,
  isDone,
  isHelper,
  onTap,
  onPreview,
}: StopCardProps) {
  const salesTypes = useSalesTypes();
  // Helpers list — used to show "Lead + 2 others" on the lead's card so
  // they know they're not flying solo on this visit.
  const helperCount = readHelpers(order.udf ?? {}).length;
  // Scheduled window from the dispatcher's UDF (FOP_START is slot offset
  // from BOARD_START_HOUR = 8 AM; FOP_DURATION is hours). When the window
  // exists, we surface it inline in the card metadata for fast scanning.
  const window = readScheduledWindow(order.udf ?? {});
  const assignment = readAssignment(order.udf ?? {});
  const durationText = assignment ? durationLabel(assignment.duration) : null;

  // Card-level phase to show:
  //  - active job: phase from the open TimeEntry (real-time, updates on
  //    Pause / Resume / Next without a refetch of the orders list)
  //  - other jobs: FOP_PHASE UDF, set on transitions; null when the job
  //    has never been started
  const cardPhase: FieldPhase | null =
    activePhase ?? toFieldPhase(order.udf?.[FOP_UDF.phase]);

  // Address line — first line of shipping address + city, truncated. Helps
  // the tech recognise the visit before tapping in.
  // Ship-to = the service address; its name is the person on site when the
  // order is billed to a builder / house account (lib/job-site.ts).
  const addressLine = jobSiteLine(order);

  // Phone for the tap-to-call icon. Populated server-side when includeUdf=true
  // (which the day list always uses). Normalise to dial-friendly digits.
  const phoneNumber = order.contactPhone ? order.contactPhone.replace(/[^\d+]/g, '') : null;

  const containerCls = isNext
    ? 'relative grid grid-cols-[6px_1fr_auto] gap-3 rounded-lg overflow-hidden cursor-pointer transition-shadow hover:shadow-md p-4'
    : isDone
      ? 'relative grid grid-cols-[6px_1fr_auto] gap-3 rounded-lg overflow-hidden border bg-surface opacity-60 cursor-not-allowed p-3'
      : 'relative grid grid-cols-[6px_1fr_auto] gap-3 rounded-lg overflow-hidden border bg-surface cursor-pointer transition-shadow hover:shadow-sm p-3';
  const style = isNext
    ? { backgroundColor: 'var(--color-live-card-bg)', color: 'white' }
    : { borderColor: 'var(--color-border-tertiary)' };

  // The type's own colour; unknown falls back to the brand blue.
  const accentColor = trade === 'unknown' ? 'var(--color-text-info)' : tradeColor(salesTypes, trade);

  return (
    <div
      role="button"
      tabIndex={isDone ? -1 : 0}
      onClick={isDone ? undefined : onTap}
      onKeyDown={(e) => {
        if (isDone) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onTap();
        }
      }}
      className={containerCls}
      style={style}
    >
      {/* Trade-color accent bar on the left edge — instant scanning
         recognition for service vs install vs PM etc. without needing to
         read the pill on the right. */}
      <div
        aria-hidden
        className="rounded-l"
        style={{ backgroundColor: isNext ? 'rgba(255,255,255,0.6)' : accentColor }}
      />

      <div className="min-w-0">
        {/* Top metadata: scheduled window + duration + trade label */}
        <div className={`flex items-center gap-1.5 flex-wrap text-[11px] ${isNext ? 'opacity-90' : 'text-ink-muted'}`}>
          {window?.label ? (
            <span className="font-medium">
              {window.label}
            </span>
          ) : (
            <span className="font-mono">
              {order.requiredDate ? order.requiredDate.slice(5) : '—'}
            </span>
          )}
          {durationText && (
            <span
              className="inline-block rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
              style={
                isNext
                  ? { backgroundColor: 'rgba(255,255,255,0.18)', color: 'white' }
                  : { backgroundColor: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)' }
              }
            >
              {durationText}
            </span>
          )}
          {!isDone && !isActive && cardPhase && cardPhase !== 'scheduled' && (
            <span className={`text-[10.5px] inline-flex items-center gap-1 ${isNext ? 'opacity-90' : ''}`}>
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: isNext ? 'rgba(255,255,255,0.85)' : 'var(--feedback-info-fg)' }}
              />
              {phaseLabel(cardPhase)}
            </span>
          )}
        </div>

        <div className={`mt-1 flex items-center gap-1.5 font-medium ${isNext ? 'text-base' : 'text-sm text-ink'}`}>
          <span className="truncate">{order.customer.name}</span>
          {isHelper && (
            <span
              className="text-[9.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
              title="You're a helper on this job — the lead drives phase + signature"
              style={
                isNext
                  ? { backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }
                  : { backgroundColor: 'var(--pill-sales-bg)', color: 'var(--pill-sales-fg)' }
              }
            >
              Helper
            </span>
          )}
          {!isHelper && helperCount > 0 && (
            <span
              className="text-[9.5px] font-semibold px-1.5 py-0.5 rounded shrink-0"
              title={`You're the lead — ${helperCount} ${helperCount === 1 ? 'helper is' : 'helpers are'} also assigned`}
              style={
                isNext
                  ? { backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }
                  : { backgroundColor: 'var(--feedback-info-bg)', color: 'var(--feedback-info-fg)' }
              }
            >
              +{helperCount}
            </span>
          )}
        </div>

        {addressLine && (
          <div className={`truncate text-[11.5px] mt-0.5 flex items-center gap-1 ${isNext ? 'opacity-85' : 'text-ink-muted'}`}>
            <MapPin className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">{addressLine}</span>
          </div>
        )}

        <div className={`mt-0.5 text-[11px] ${isNext ? 'opacity-85' : 'text-ink-muted'}`}>
          <span className="font-mono">{order.orderNo}</span> · ${order.total}
        </div>

        {isActive && cardPhase && (
          <div className={`mt-1 text-[11px] inline-flex items-center gap-1.5 ${isNext ? 'opacity-95' : ''}`}>
            <span
              aria-hidden
              className="inline-block h-1.5 w-1.5 rounded-full animate-pulse"
              style={{ backgroundColor: isNext ? 'rgba(255,255,255,0.85)' : 'var(--feedback-info-fg)' }}
            />
            <span>{phaseLabel(cardPhase)}</span>
            {liveSec !== null && (
              <span className="font-mono tabular-nums ml-1">{formatTimer(liveSec)}</span>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col items-end gap-1 shrink-0">
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label={`Preview ${order.orderNo}`}
            title="Preview job details"
            className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
              isNext ? 'text-white hover:bg-white/20' : 'text-ink-muted hover:bg-surface-muted hover:text-ink'
            }`}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
          </button>
          {phoneNumber && !isDone && (
            <a
              href={`tel:${phoneNumber}`}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              aria-label={`Call ${order.customer.name}`}
              title={`Call ${order.contactPhone ?? phoneNumber}`}
              className={`inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                isNext ? 'text-white hover:bg-white/20' : 'text-ink-muted hover:bg-surface-muted hover:text-ink'
              }`}
            >
              <Phone className="h-3.5 w-3.5" aria-hidden />
            </a>
          )}
        </div>
        {isDone ? (
          <Check className="h-4 w-4 text-emerald-700" aria-hidden />
        ) : isActive && cardPhase ? (
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
            style={{ backgroundColor: 'rgba(255,255,255,0.18)', color: 'white' }}
          >
            {phaseLabel(cardPhase)}
          </span>
        ) : isNext ? (
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
            style={{ backgroundColor: 'rgba(255,255,255,0.18)', color: 'white' }}
          >
            {tradeLabel(salesTypes, trade)}
          </span>
        ) : cardPhase && cardPhase !== 'scheduled' ? (
          <Pill variant={phasePillVariant(cardPhase)}>{phaseLabel(cardPhase)}</Pill>
        ) : (
          <TradePill types={salesTypes} trade={trade} />
        )}
        {!isDone && (
          <ChevronRight
            className={isNext ? 'h-4 w-4 opacity-80' : 'h-4 w-4 text-ink-subtle'}
            aria-hidden
          />
        )}
      </div>
    </div>
  );
}

interface DayTotals {
  /** Sum of FOP_DURATION across in-progress stops. */
  scheduledHours: number;
  /** Sum of order totals across in-progress stops (dollars). */
  totalDollars: number;
}

function computeDayTotals(orders: ReadonlyArray<SalesOrderSummary>): DayTotals {
  let scheduledHours = 0;
  let totalDollars = 0;
  for (const o of orders) {
    const a = readAssignment(o.udf ?? {});
    if (a) scheduledHours += a.duration;
    const t = Number(o.total);
    if (Number.isFinite(t)) totalDollars += t;
  }
  return { scheduledHours, totalDollars };
}

function formatThousands(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/** The scheduled window ("8a – 12p") from the order's assignment, or null when unscheduled. */
function readScheduledWindow(udf: Record<string, string>): { label: string } | null {
  const a = readAssignment(udf);
  if (!a) return null;
  return { label: windowLabel(a.startHour, a.duration) };
}

function toFieldPhase(value: string | undefined): FieldPhase | null {
  if (!value) return null;
  switch (value) {
    case 'scheduled':
    case 'traveling':
    case 'on_site':
    case 'work_in_progress':
    case 'paused':
    case 'work_completed':
    case 'signed_off':
      return value;
    default:
      return null;
  }
}

function DayListSkeleton() {
  return (
    <ul className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i}>
          <div className="h-16 rounded-lg border bg-surface animate-pulse" style={{ borderColor: 'var(--color-border-tertiary)' }} />
        </li>
      ))}
    </ul>
  );
}

/** Pre-confirm payload assembled from a card's metadata. We don't refetch
 * the order here — the card already has enough to surface customer name,
 * scheduled window, address, and trade in the confirm sheet. */
interface PendingStart {
  spireId: number;
  orderNo: string;
  customerName: string;
  scheduledWindowLabel: string | null;
  addressLine: string | null;
  trade: Trade;
}

function buildPendingStart(salesTypes: ReadonlyArray<SalesType>, order: SalesOrderSummary, typeUdfKey: string): PendingStart {
  const trade = orderTrade(salesTypes, order, typeUdfKey);
  const a = readAssignment(order.udf ?? {});
  const scheduledWindowLabel = a
    ? `${windowLabel(a.startHour, a.duration)} · ${durationLabel(a.duration)}`
    : null;
  // Ship-to = the service address; its name is the person on site when the
  // order is billed to a builder / house account (lib/job-site.ts).
  const addressLine = jobSiteLine(order);
  return {
    spireId: order.spireId,
    orderNo: order.orderNo,
    customerName: order.customer.name,
    scheduledWindowLabel,
    addressLine,
    trade,
  };
}

function sortByRequiredDate(a: SalesOrderSummary, b: SalesOrderSummary): number {
  // Sort earlier required dates first; orders without one sink to the bottom.
  const av = a.requiredDate ?? '9999-12-31';
  const bv = b.requiredDate ?? '9999-12-31';
  return av.localeCompare(bv);
}

/**
 * Decompose in-progress stops by their dispatched FOP_DATE relative to
 * today. The today bucket drives stats + the "next" highlight; overdue
 * surfaces as a warning section at the top; future days are individually
 * grouped so the tech can peek tomorrow / Friday without losing focus.
 */
interface GroupedDay {
  date: string;
  orders: ReadonlyArray<SalesOrderSummary>;
}

interface GroupedDayList {
  overdue: ReadonlyArray<SalesOrderSummary>;
  undated: ReadonlyArray<SalesOrderSummary>;
  today: ReadonlyArray<SalesOrderSummary>;
  future: ReadonlyArray<GroupedDay>;
}

function groupByDate(
  orders: ReadonlyArray<SalesOrderSummary>,
  todayIso: string,
): GroupedDayList {
  const overdue: SalesOrderSummary[] = [];
  const undated: SalesOrderSummary[] = [];
  const today: SalesOrderSummary[] = [];
  const futureMap = new Map<string, SalesOrderSummary[]>();

  for (const order of orders) {
    const a = readAssignment(order.udf ?? {});
    if (!a) {
      undated.push(order);
      continue;
    }
    if (a.date < todayIso) {
      overdue.push(order);
    } else if (a.date === todayIso) {
      today.push(order);
    } else {
      const existing = futureMap.get(a.date) ?? [];
      existing.push(order);
      futureMap.set(a.date, existing);
    }
  }

  const future: GroupedDay[] = Array.from(futureMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, orders]) => ({ date, orders }));

  return { overdue, undated, today, future };
}

/**
 * Friendly label for a future date. Uses "Tomorrow" for tomorrow; for
 * later days inside the same week, weekday + month/day (e.g. "Fri · Jun 5");
 * otherwise an absolute month/day/year.
 */
/**
 * The tech's own booked time off + company holidays for the next 60 days.
 * Read-only here — the office books it (Admin → Skills matrix → Edit tech).
 * Hidden entirely when there's nothing coming up so the Day stays lean.
 */
function DaysOffCard({ techId, todayIso }: { techId: string; todayIso: string }) {
  const horizon = shiftIsoDate(todayIso, 60);
  const avail = useAvailabilityWindow(todayIso, horizon, techId);
  const [open, setOpen] = useState(false);
  // workDays isn't needed here — a tech knows their own week; this card is
  // for the exceptions (booked time off + holidays) they might forget.
  const today: Unavailable | null = unavailability({ workDays: [0, 1, 2, 3, 4, 5, 6] }, todayIso, avail.timeOff, avail.holidays);
  const upcoming: Array<{ key: string; when: string; label: string }> = [
    ...avail.timeOff.map((t) => ({
      key: `t-${t.id}`,
      start: t.startDate,
      when:
        (t.endDate !== t.startDate ? `${shortDay(t.startDate)} – ${shortDay(t.endDate)}` : shortDay(t.startDate)) +
        (t.startTime && t.endTime ? `, ${t.startTime}–${t.endTime}` : ''),
      label: t.reason?.trim() || 'Time off',
    })),
    ...avail.holidays.map((h) => ({ key: `h-${h.date}`, start: h.date, when: shortDay(h.date), label: h.name })),
  ]
    .sort((a, b) => a.start.localeCompare(b.start))
    .map(({ key, when, label }) => ({ key, when, label }));

  if (!today && upcoming.length === 0) return null;

  return (
    <div
      className="rounded-lg border bg-surface"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      {today && (
        <div className="px-3 py-2 text-sm text-ink border-b flex items-center gap-2" style={{ borderColor: 'var(--color-border-tertiary)', backgroundColor: 'var(--feedback-info-bg)' }}>
          <CalendarOff className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            You're booked off {today.kind === 'time_off' && today.hours ? 'for part of today' : 'today'}
            {today.kind === 'holiday' ? ` — ${today.label}` : today.kind === 'time_off' && (today.hours || today.reason) ? ` — ${today.label}` : ''}
            . Anything listed below is still assigned to you; check with dispatch if that's unexpected.
          </span>
        </div>
      )}
      {upcoming.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="w-full flex items-center justify-between px-3 py-2 text-xs text-ink-muted"
          >
            <span className="inline-flex items-center gap-1.5">
              <CalendarOff className="h-3.5 w-3.5" aria-hidden />
              Days off in the next 60 days ({upcoming.length})
            </span>
            {open ? <ChevronUp className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
          </button>
          {open && (
            <ul className="px-3 pb-2 space-y-1 text-sm">
              {upcoming.map((u) => (
                <li key={u.key} className="flex justify-between gap-3">
                  <span className="text-ink">{u.label}</span>
                  <span className="text-ink-muted tabular-nums">{u.when}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function shortDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * Field-created jobs still in the outbox (no signal, or Spire hasn't
 * answered yet). Shown so the tech sees the job exists before the order
 * number does; the card is replaced by the real one on the next refetch.
 */
function QueuedFieldJobs({ rows }: { rows: SalesOrderOutboxEntry[] }) {
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-ink-subtle">Queued — syncing to Spire</div>
      {rows.map((r) => (
        <div
          key={r.entryId}
          className="rounded-lg border bg-surface px-3 py-2 text-sm"
          style={{ borderColor: 'var(--color-border-tertiary)', borderStyle: 'dashed' }}
        >
          <div className="flex items-center gap-2">
            <Loader2 className={r.status === 'failed' ? 'h-3.5 w-3.5 text-red-600' : 'h-3.5 w-3.5 animate-spin text-ink-muted'} aria-hidden />
            <span className="font-medium text-ink truncate">{r.body.shippingAddress?.name ?? r.body.customerNo}</span>
            <span className="ml-auto text-xs text-ink-muted tabular-nums">
              {r.body.udf?.[FOP_UDF.date]} {r.body.udf?.[FOP_UDF.start]}
            </span>
          </div>
          <div className="text-xs text-ink-muted truncate">
            {[r.body.shippingAddress?.line1, r.body.shippingAddress?.city].filter(Boolean).join(', ')}
            {r.body.customerPO ? ` · PO ${r.body.customerPO}` : ''}
          </div>
          {r.status === 'failed' && r.lastError && (
            <div className="text-xs text-red-700 mt-1">Not created: {r.lastError} — see Me → Outbox.</div>
          )}
        </div>
      ))}
    </div>
  );
}

function friendlyFutureLabel(iso: string, todayIso: string): string {
  if (iso === shiftIsoDate(todayIso, 1)) return 'Tomorrow';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const [ty, tm, td] = todayIso.split('-').map(Number);
  const today = new Date(ty!, (tm ?? 1) - 1, td ?? 1);
  const diffDays = Math.round((dt.getTime() - today.getTime()) / 86_400_000);
  const weekday = dt.toLocaleDateString(undefined, { weekday: 'short' });
  const monthDay = dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (diffDays <= 6) return `${weekday} · ${monthDay}`;
  return `${weekday} · ${monthDay}, ${dt.getFullYear()}`;
}

/**
 * One date-bucket section in the Day list. Renders a clickable header
 * (with count + collapse chevron) and a list of StopCards when expanded.
 * Tone controls header color: primary (Today, brand), warning (Overdue),
 * info (Date not set), muted (Tomorrow / later).
 */
interface DayBucketProps {
  title: string;
  tone: 'primary' | 'warning' | 'info' | 'muted';
  orders: ReadonlyArray<SalesOrderSummary>;
  defaultExpanded: boolean;
  activeSpireId: number | null;
  activePhase: FieldPhase | null;
  liveSec: number;
  onTap: (spireId: number, orderNo: string) => void;
  onPreview: (spireId: number, orderNo: string, customerName: string) => void;
  /** The viewer's tech id, used to flag cards where they're a helper
   * rather than the lead. Null when the user isn't linked to a tech yet. */
  viewerTechId: string | null;
  /** When true, the first non-active card in this bucket gets the live
   * "next stop" treatment (used for Today). */
  highlightFirstAsNext?: boolean;
  /** Shown in place of an empty list when the bucket has 0 orders. */
  emptyHint?: string | null;
}

function DayBucket({
  title,
  tone,
  orders,
  defaultExpanded,
  activeSpireId,
  activePhase,
  liveSec,
  onTap,
  onPreview,
  viewerTechId,
  highlightFirstAsNext,
  emptyHint,
}: DayBucketProps) {
  const salesTypes = useSalesTypes();
  const typeUdfKey = useFieldJobsSettings().typeUdfKey;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const toneClasses = tone === 'warning'
    ? 'text-amber-700'
    : tone === 'info'
      ? 'text-ink-accent'
      : tone === 'primary'
        ? 'text-ink'
        : 'text-ink-muted';

  if (orders.length === 0 && !emptyHint) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`w-full flex items-center justify-between text-xs uppercase tracking-wider mb-2 hover:opacity-80 ${toneClasses}`}
        aria-expanded={expanded}
      >
        <span className="font-semibold">
          {title} {orders.length > 0 && <span className="opacity-75">({orders.length})</span>}
        </span>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5" aria-hidden />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden />
        )}
      </button>
      {expanded && (
        <>
          {orders.length === 0 && emptyHint && (
            <div className="text-[12px] text-ink-muted italic px-1 py-2">{emptyHint}</div>
          )}
          {orders.length > 0 && (
            <ul className="space-y-2">
              {orders.map((order, idx) => {
                const trade = orderTrade(salesTypes, order, typeUdfKey);
                const isActive = activeSpireId === order.spireId;
                // "Next" treatment goes to the active card (wherever it
                // lives) OR — when no job is active — the first card in
                // the bucket flagged with `highlightFirstAsNext` (i.e.
                // Today). Past buckets / future days never auto-highlight.
                const isFirstInBucket = idx === 0 && activeSpireId === null && highlightFirstAsNext === true;
                // Multi-tech: a helper sees the job in their My Day too,
                // but they're not the lead — surface a "Helper" pill so
                // they know the workflow ownership (phase mirror,
                // signature) sits with the assigned lead.
                const leadId = order.udf?.[FOP_UDF.tech]?.trim() ?? null;
                const isHelper = viewerTechId !== null && leadId !== null && leadId !== viewerTechId;
                return (
                  <li key={order.spireId}>
                    <StopCard
                      order={order}
                      trade={trade}
                      isNext={isActive || isFirstInBucket}
                      isActive={isActive}
                      liveSec={isActive ? liveSec : null}
                      activePhase={isActive ? activePhase : null}
                      isDone={false}
                      isHelper={isHelper}
                      onTap={() => onTap(order.spireId, order.orderNo)}
                      onPreview={() => onPreview(order.spireId, order.orderNo, order.customer.name)}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
